import TimerSurfaceService from '../../services/TimerSurfaceService';

// Only one explicit duration or increasing range after a cooking/wait verb qualifies.
// Temperature, vague endpoints and multiple waits must not become guesses.
export function extractTimedWait(step) {
  if (typeof step !== 'string') return null;
  const ranges = [...step.matchAll(/\b(\d{1,3})\s*(?:-|–|to)\s*(\d{1,3})\s*(seconds?|secs?|minutes?|mins?|hours?|hrs?)\b/gi)];
  if (ranges.length > 1) return null;
  if (ranges.length && (Number(ranges[0][1]) < 1 || Number(ranges[0][2]) <= Number(ranges[0][1]))) return null;
  const timedStep = ranges.length ? step.replace(ranges[0][0], `${ranges[0][2]} ${ranges[0][3]}`) : step;
  const matches = [...timedStep.matchAll(/\b(?:cook|simmer|boil|bake|roast|heat|steam|steep|chill|refrigerate|freeze|rest|let\s+(?:it|the\s+\w+|\w+)\s+rest|wait|leave|soak)\b[^.!?;]{0,85}?\b(?:for|about|approximately)\s+(\d{1,3})\s*(seconds?|secs?|minutes?|mins?|hours?|hrs?)\b/gi)];
  if (matches.length !== 1 || (!ranges.length && /\b\d+\s*(?:-|–|to)\s*\d+\s*(?:seconds?|minutes?|hours?|secs?|mins?|hrs?)\b/i.test(step))) return null;
  // A second duration (even without a cooking verb), or a sequence of actions,
  // cannot be assigned reliably to this one timer. Fail closed.
  const durations = [...timedStep.matchAll(/\b\d{1,3}\s*(?:seconds?|secs?|minutes?|mins?|hours?|hrs?)\b/gi)];
  if (durations.length !== 1 || /\b(?:then|or)\b/i.test(step)) return null;
  const [match, amount, unit] = matches[0];
  if (/\b(?:at|to)\s*\d+\s+(?:for|about|approximately)\b/i.test(match)) return null;
  const value = Number(amount) * (/^h/i.test(unit) ? 3600000 : /^m/i.test(unit) ? 60000 : 1000);
  return value > 0 && value <= 24 * 3600000 ? value : null;
}

export function extractTimedWaitBounds(step) {
  const finalMs = extractTimedWait(step);
  if (!finalMs) return null;
  const range = step.match(/\b(\d{1,3})\s*(?:-|–|to)\s*\d{1,3}\s*(seconds?|secs?|minutes?|mins?|hours?|hrs?)\b/i);
  const firstMs = range ? Number(range[1]) * (/^h/i.test(range[2]) ? 3600000 : /^m/i.test(range[2]) ? 60000 : 1000) : finalMs;
  return { firstMs, finalMs };
}

function hash(value) {
  // Opaque storage identifiers; no raw user/recipe names in localStorage keys.
  let h = 2166136261;
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(36);
}

export const recipeTimerScope = userId => `recipe-timers-v1-${hash(String(userId))}`;
export function recipeTimerId(userId, mealId, instructions, index) {
  if (!userId || !mealId || !Array.isArray(instructions)) return null;
  return `recipe-${hash(JSON.stringify([String(userId), String(mealId), instructions, index]))}`;
}

export function createRecipeTimerRegistry({ storage, now = () => Date.now(), publish = () => {}, canPublish = () => true }) {
  const key = userId => recipeTimerScope(userId);
  const read = userId => {
    try {
      const value = JSON.parse(storage.getItem(key(userId)) || '{}');
      return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
    } catch (_) { recipeTimerPersistenceAvailable = false; return {}; }
  };
  const valid = item => item && Number.isFinite(item.targetDurationMs) && item.targetDurationMs > 0 &&
    (item.firstDurationMs === undefined || (Number.isFinite(item.firstDurationMs) && item.firstDurationMs > 0 && item.firstDurationMs <= item.targetDurationMs)) &&
    Number.isFinite(item.remainingMs) && item.remainingMs >= 0 &&
    (item.deadlineEpochMs === null || Number.isFinite(item.deadlineEpochMs));
  const snapshot = (item, time = now()) => {
    if (!valid(item)) return null;
    const remainingMs = item.deadlineEpochMs === null ? item.remainingMs : Math.max(0, item.deadlineEpochMs - time);
    const firstDurationMs = item.firstDurationMs ?? item.targetDurationMs;
    return { ...item, firstDurationMs, remainingMs,
      firstReached: remainingMs <= item.targetDurationMs - firstDurationMs,
      running: item.deadlineEpochMs !== null && remainingMs > 0,
      completed: item.deadlineEpochMs !== null && remainingMs === 0 };
  };
  const all = userId => read(userId);
  const get = (userId, id) => snapshot(read(userId)[id]);
  let publishGuard = canPublish;
  const sync = (userId, { authorized = false } = {}) => {
    if (!userId || (!authorized && !publishGuard(userId))) return { ok: false, reason: 'inactive-scope' };
    // Preserve a just-completed timer briefly for its native notification and red PiP row,
    // but do not accumulate old acknowledgements in the bounded native alarm ledger.
    const timers = Object.entries(read(userId)).filter(([, item]) => valid(item) && item.deadlineEpochMs !== null &&
      (!item.alerted || item.deadlineEpochMs >= now() - 60000))
      .map(([id, item]) => ({ id, title: item.title, targetDurationMs: item.targetDurationMs,
        firstDurationMs: item.firstDurationMs ?? item.targetDurationMs,
        firstDeadlineEpochMs: item.deadlineEpochMs - item.targetDurationMs + (item.firstDurationMs ?? item.targetDurationMs),
        firstAlerted: Boolean(item.firstAlerted), deadlineEpochMs: item.deadlineEpochMs, running: !item.alerted }));
    try { storage.setItem('recipe-timers-active-scope', key(userId)); } catch (_) { /* private mode */ }
    return publish({ scopeId: key(userId), timers });
  };
  const save = (userId, id, item) => {
    if (!publishGuard(userId)) return;
    try { storage.setItem(key(userId), JSON.stringify({ ...read(userId), [id]: item })); } catch (_) { recipeTimerPersistenceAvailable = false; }
    sync(userId);
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('recipe-timers-changed', { detail: { scopeId: key(userId) } }));
  };
  return {
    all, get, sync,
    setPublishGuard(guard) { publishGuard = guard; },
    start(userId, id, title, targetDurationMs, firstDurationMs = targetDurationMs) {
      if (!userId || !id || !Number.isFinite(targetDurationMs) || targetDurationMs <= 0 ||
          !Number.isFinite(firstDurationMs) || firstDurationMs <= 0 || firstDurationMs > targetDurationMs) return;
      const previous = get(userId, id);
      const remainingMs = previous?.remainingMs > 0 ? previous.remainingMs : targetDurationMs;
      save(userId, id, { title, targetDurationMs, firstDurationMs, remainingMs, deadlineEpochMs: now() + remainingMs,
        firstAlerted: Boolean(previous?.firstAlerted && remainingMs < targetDurationMs) });
    },
    pause(userId, id) {
      const item = get(userId, id);
      if (item?.running) save(userId, id, { ...item, deadlineEpochMs: null });
    },
    resume(userId, id) {
      const item = get(userId, id);
      if (item && !item.completed && !item.running && item.remainingMs > 0)
        save(userId, id, { ...item, deadlineEpochMs: now() + item.remainingMs });
    },
    reset(userId, id) {
      const item = get(userId, id);
      if (item) save(userId, id, { ...item, remainingMs: item.targetDurationMs, deadlineEpochMs: null, firstAlerted: false, alerted: false });
    },
    markFirstAlerted(userId, id) {
      const item = get(userId, id);
      if (item?.firstReached && item.firstDurationMs < item.targetDurationMs && !item.firstAlerted)
        save(userId, id, { ...item, firstAlerted: true });
    },
    markAlerted(userId, id) {
      const item = get(userId, id);
      if (item?.completed && !item.alerted) save(userId, id, { ...item, alerted: true });
    },
  };
}

export let recipeTimerPersistenceAvailable = true;
function browserStorage() {
  try {
    const store = window.localStorage;
    const probe = 'recipe-timers-storage-probe';
    store.setItem(probe, '1');
    store.removeItem(probe);
    return store;
  } catch (_) {
    recipeTimerPersistenceAvailable = false;
    const memory = new Map();
    return { getItem: key => memory.get(key) ?? null, setItem: (key, value) => memory.set(key, value), removeItem: key => memory.delete(key) };
  }
}

export const recipeTimerRegistry = createRecipeTimerRegistry({
  storage: typeof window === 'undefined' ? { getItem: () => null, setItem: () => {} } : browserStorage(),
  publish: payload => TimerSurfaceService.updateRecipeTimers(payload),
});
